import React, { useEffect, useState } from 'react';
import '../styles/BlogPage.css';
import BlogHeroSection from '../components/BlogPage/BlogHeroSection';
import BlogIntroSection from '../components/BlogPage/BlogIntroSection';
import BlogCategoryFilter from '../components/BlogPage/BlogCategoryFilter';
import BlogPostsGrid from '../components/BlogPage/BlogPostsGrid';
import BlogPagination from '../components/BlogPage/BlogPagination';
import BlogBoatCTA from '../components/BlogPage/BlogBoatCTA';
import BlogShareExperienceCTA from '../components/BlogPage/BlogShareExperienceCTA';
import WebPreloader from '../../core/components/Common/WebPreloader';
import SEOHead from '../../core/components/Common/SEOHead';
import BlogService from '../services/BlogService';

const BlogPage = () => {
  const [showPreloader, setShowPreloader] = useState(true);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const perPage = 12;

  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch posts
  const fetchPosts = async () => {
    try {
      setLoading(true);
      console.log('DEBUG: Fetching posts with category:', selectedCategory);
      const data = await BlogService.fetchPosts(currentPage, perPage, selectedCategory);
      
      console.log('DEBUG: Received data:', data);
      
      // Backend returns posts directly, not wrapped in pagination object
      // So we need to handle the response accordingly
      if (Array.isArray(data)) {
        // If data is an array, it's the posts directly
        setPosts(data);
        setTotalPages(1); // No pagination for now
        setTotalPosts(data.length);
        console.log('DEBUG: Set posts array, count:', data.length);
      } else if (data && data.posts) {
        // If data has posts property (future pagination support)
        setPosts(data.posts);
        setTotalPages(data.total_pages || 1);
        setTotalPosts(data.total_posts || data.posts.length);
        console.log('DEBUG: Set posts from data.posts, count:', data.posts.length);
      } else {
        // Fallback
        setPosts([]);
        setTotalPages(1);
        setTotalPosts(0);
        console.log('DEBUG: No posts found, set empty array');
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Error loading blog posts');
      setPosts([]);
      setTotalPages(1);
      setTotalPosts(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const data = await BlogService.fetchCategories();
      console.log('DEBUG: Fetched categories:', data);
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, currentPage]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error) {
    return (
      <div className="blog-page">
        <BlogHeroSection />
        <div className="blog-page__error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <SEOHead />
      {showPreloader && <WebPreloader />}
      
      <BlogHeroSection />
      
      <BlogIntroSection />
      
      <div className="blog-page__content">
        <div className="container">
          <div className="blog-page__filters">
            {console.log('DEBUG: Rendering filters with categories:', categories)}
            <BlogCategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
          </div>
          
          <div className="blog-posts-container">
            <BlogPostsGrid
              posts={posts}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
          
          {/* Boat Rental CTA */}
          <BlogBoatCTA />
          
          {totalPages > 1 && (
            <BlogPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
          
          {/* Share Experience CTA */}
          <BlogShareExperienceCTA />
        </div>
      </div>
    </div>
  );
};

export default BlogPage; 